/**
 * Pretty-print HTML: one block element per line, nested content indented.
 *
 * Inline runs (`<strong>`, `<a>`, text) are kept on a single line so words never
 * get split across lines — re-indenting inline content would change how the
 * rendered text spaces out.
 */

import { parseToBody } from './sanitize';
import { INLINE_TAGS } from './clean';

const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** Blocks whose exact whitespace is content and must be left alone. */
const PREFORMATTED = new Set(['pre', 'textarea']);

export function tidyHtml(html: string, indent = '  '): string {
  const body = parseToBody(html);
  const lines: string[] = [];
  renderChildren(body, 0, lines, indent);
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function renderChildren(parent: ParentNode, depth: number, lines: string[], indent: string): void {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data.replace(/\s+/g, ' ').trim();
      if (text) lines.push(indent.repeat(depth) + text);
      continue;
    }

    if (node.nodeType === Node.COMMENT_NODE) {
      lines.push(`${indent.repeat(depth)}<!--${(node as Comment).data}-->`);
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const pad = indent.repeat(depth);

    if (VOID_TAGS.has(tag)) {
      lines.push(pad + openTag(el));
      continue;
    }

    if (PREFORMATTED.has(tag)) {
      // Emit verbatim — the first line carries the indent, the rest do not.
      lines.push(pad + openTag(el) + el.innerHTML + `</${tag}>`);
      continue;
    }

    // An element holding only inline content stays on one line.
    if (isInlineOnly(el)) {
      lines.push(pad + openTag(el) + inlineHtml(el) + `</${tag}>`);
      continue;
    }

    lines.push(pad + openTag(el));
    renderChildren(el, depth + 1, lines, indent);
    lines.push(pad + `</${tag}>`);
  }
}

function isInlineOnly(el: Element): boolean {
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = (child as Element).tagName.toLowerCase();
      if (!INLINE_TAGS.has(tag)) return false;
      if (!isInlineOnly(child as Element)) return false;
    }
  }
  return true;
}

/** Inline HTML with runs of whitespace collapsed but single spaces preserved. */
function inlineHtml(el: Element): string {
  return el.innerHTML.replace(/\s+/g, ' ').trim();
}

function openTag(el: Element): string {
  let out = `<${el.tagName.toLowerCase()}`;
  for (const attr of Array.from(el.attributes)) {
    out += attr.value === '' ? ` ${attr.name}` : ` ${attr.name}="${escapeAttr(attr.value)}"`;
  }
  return out + '>';
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
